<template>
    <pendo-modal
        :visible="visible"
        :title="`Upload list of ${kind}s`"
        :append-to-body="false"
        :show-backdrop="false"
        width="470px"
        height="auto"
        class="csv-upload-modal"
        @close="$emit('cancel')"
        @closed="reset">
        <template #body>
            <input
                ref="fileInput"
                type="file"
                hidden="true"
                @change="onFileChange($event.target)">
            <div v-if="kind == 'visitor'">
                <p>
                    Upload a CSV file to create a static segment of visitors that you can use for analytics and guide
                    delivery.
                </p>
                <p>
                    Visitors you upload who haven’t used your app yet won’t show as eligible in segments until they
                    become active.
                </p>
            </div>
            <p v-else>
                Select and upload a file to create segments or target guides to a specific, static group of {{ kind }}s.
            </p>
            <div
                class="flat-well"
                :class="{
                    failed: state === 'failed' || state === 'conflict',
                    success: state === 'complete',
                }">
                <div v-if="state === 'uploading'">
                    <div>Uploading {{ file.name }}...</div>
                    <pendo-progress-bar :value="percentComplete" />
                </div>
                <pendo-alert
                    v-else-if="state === 'complete'"
                    type="success">
                    <span><b>{{ file.name }}</b> uploaded successfully</span>
                </pendo-alert>
                <pendo-alert
                    v-else-if="state === 'conflict'"
                    type="error">
                    <div>
                        Another list is currently being uploaded to your account. Please retry your upload again in a
                        few minutes.
                    </div>
                </pendo-alert>
                <pendo-alert
                    v-else-if="state === 'failed'"
                    type="error">
                    <div v-if="!csvLimitExceeded && !csvFieldIssueFound">
                        <strong>{{ file.name }}</strong> failed to upload.
                    </div>
                    <div v-if="csvLimitExceeded">
                        CSV exceeds the amount of supported entries ({{ csvLimit }}).
                    </div>
                    <div v-if="csvFieldIssueFound">
                        <strong>{{ file.name }}</strong>: {{ csvFieldIssue }}
                    </div>
                </pendo-alert>
                <div v-else>
                    <pendo-button
                        theme="app"
                        type="primary"
                        label="Choose file"
                        @click="chooseFile" />
                    <span v-if="file">{{ file.name }}</span>
                    <pendo-checkbox
                        v-model="excludeHeader"
                        label="Exclude row 1 (column header)" />
                </div>
            </div>
            <pendo-alert
                v-if="uploadComplete"
                type="info">
                Only visitor IDs with activity will be included in the eligible visitors count.
            </pendo-alert>
            <pendo-alert
                v-if="analyticsDelayed"
                type="warning">
                We’re processing your CSV file. Eligible visitors appear as they are processed. Large segments may take
                several minutes to complete.
            </pendo-alert>
            <div v-if="state === 'ready'">
                <strong>Note:</strong> File must be new line delimited.
            </div>
        </template>
        <template #footer>
            <div class="csv-upload-modal__footer">
                <pendo-button
                    theme="app"
                    type="secondary"
                    label="Cancel"
                    @click="$emit('cancel')" />
                <pendo-button
                    v-if="state === 'complete'"
                    theme="app"
                    type="primary"
                    label="Insert list"
                    @click="insertList" />
                <pendo-button
                    v-else-if="state === 'failed'"
                    theme="app"
                    type="primary"
                    label="Retry upload"
                    @click="reset" />
                <pendo-button
                    v-else
                    theme="app"
                    :disabled="state === 'uploading'"
                    :loading="state === 'uploading'"
                    type="primary"
                    label="Upload file"
                    @click="uploadFile" />
            </div>
        </template>
    </pendo-modal>
</template>

<script>
import { upload, WARNING_TYPES } from '@/segments/csv-upload-service';
import { PendoAlert, PendoButton, PendoCheckbox, PendoModal, PendoProgressBar } from '@pendo/components';

export default {
    name: 'CsvUploadModal',
    components: {
        PendoProgressBar,
        PendoButton,
        PendoCheckbox,
        PendoModal,
        PendoAlert,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        kind: {
            type: String,
            required: true,
        },
        metadataField: {
            type: String,
            default: null,
        },
    },
    emits: ['cancel', 'close'],
    data () {
        return {
            state: 'ready',
            status: {},
            file: null,
            percentComplete: 0,
            excludeHeader: false,
            tagId: null,
            csvLimit: null,
            csvLimitExceeded: false,
            csvFieldIssue: null,
            csvFieldIssueFound: false,
            uploadStatusInterval: null,
        };
    },
    computed: {
        analyticsDelayed () {
            return this.status?.warnings?.some(({ type }) => type === WARNING_TYPES.ANALYTICS_DELAYED) || false;
        },
        uploadComplete () {
            return this.state === 'complete';
        },
    },
    methods: {
        chooseFile () {
            this.$refs.fileInput.click();
        },
        onFileChange (event) {
            this.file = event.files[0];
        },
        reset () {
            window.clearInterval(this.uploadStatusInterval);
            this.state = 'ready';
            this.status = {};
            this.file = null;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
            this.percentComplete = 0;
            this.excludeHeader = false;
            this.tagId = null;
            this.csvLimitExceeded = false;
            this.csvFieldIssueFound = false;
        },
        async uploadFile () {
            const { file } = this;
            const notify = (progress) => {
                if (typeof progress === 'number') {
                    this.percentComplete = progress;
                } else if (progress.command === 'finish') {
                    this.state = 'complete';
                    this.status = progress;
                } else if (progress.command === 'error') {
                    this.handleFileUploadErrors(progress);
                }
            };

            if (file) {
                this.state = 'uploading';
                this.percentComplete = 0;

                try {
                    const uploadParams = {
                        excludeHeader: this.excludeHeader,
                    };
                    if (this.metadataField) {
                        uploadParams.metadataField = this.metadataField;
                    }
                    const { entityTagId, uploadPromise, pollingInterval } = await upload(this.kind, file, notify, uploadParams);

                    this.uploadStatusInterval = pollingInterval;

                    await uploadPromise;
                    this.tagId = entityTagId;
                } catch (err) {
                    this.handleFileUploadErrors(err);
                }
            }
        },
        handleFileUploadErrors (error) {
            const { status, error: errorMessage } = error;
            switch (status) {
                case 400:
                    if (errorMessage.includes('Csv contains greater than')) {
                        try {
                            // format string as millions
                            this.csvLimit = errorMessage.match(/[0-9]+/)[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                        } catch {
                            this.csvLimit = '10,000,000';
                        }
                        this.csvLimitExceeded = true;
                    }
                    if (errorMessage.includes('wrong number of fields')) {
                        try {
                            this.csvFieldIssue = `Incorrect number of fields in CSV: ${
                                errorMessage.match(/line [0-9]+/)[0]
                            }. Please check that you have 1 field per row and re-upload.`;
                        } catch {
                            this.csvFieldIssue =
                                'Incorrect number of fields in CSV. Please check that you have 1 field per row and re-upload.';
                        }
                        this.csvFieldIssueFound = true;
                    }
                    this.state = 'failed';
                    break;
                case 409:
                    this.state = 'conflict';
                    break;
                default:
                    this.state = 'failed';
            }
        },
        insertList () {
            this.$emit('close', {
                analyticsDelayedAtTimestamp: this.analyticsDelayed ? this.status.lastUpdatedAt : null,
                tagId: this.tagId,
                value: this.file.name,
            });
        },
    },
};
</script>

<style scoped lang="less">
.csv-upload-modal {
    /* Modals on drawers on modals prevent the built-in backdrop from appearing at the right z-index */
    background: rgba(var(--raw-palette-gray-100), 0.75);

    .pendo-checkbox {
        display: flex;
        width: fit-content;
        margin-top: 10px;
    }

    input[type='file'][hidden] {
        display: none;
    }

    .flat-well {
        &.success,
        &.failed {
            padding: 0;
            border: 0;
        }

        &.failed {
            .conflict {
                > * {
                    display: inline-block;
                    vertical-align: top;
                }

                div {
                    width: 320px;
                }
            }
        }
    }

    .csv-upload-modal__footer {
        display: flex;
        justify-content: flex-end;
        flex-direction: row;
        align-items: center;
    }
}
</style>
